// External imports
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Internal imports
import { useAuth } from "@/providers/auth.provider";
import { heartsService, type RestaurantHearts } from "../services/hearts.service";

export const heartsQueryKey = (restaurantId: string) => ["hearts", restaurantId] as const;

type Summary = { count: number; heartedByMe: boolean };

/** Who hearted a restaurant and whether you did. */
export const useRestaurantHearts = (restaurantId: string, enabled = true) =>
  useQuery({
    queryKey: heartsQueryKey(restaurantId),
    queryFn: () => heartsService.getForRestaurant(restaurantId),
    enabled: enabled && !!restaurantId,
    staleTime: 60 * 1000,
  });

/**
 * Rewrites the heart summary of one restaurant everywhere table rows come from:
 * the saved-restaurants pages and the list-detail items. Unknown shapes are left untouched.
 */
function patchRowSummaries(queryClient: QueryClient, restaurantId: string, next: (prev: Summary) => Summary) {
  const patch = (hearts: Summary | undefined) => next(hearts ?? { count: 0, heartedByMe: false });

  queryClient.setQueriesData<{ pages?: { saved?: { restaurant: { id: string }; hearts?: Summary }[] }[] }>(
    { queryKey: ["saved-restaurants"] },
    (old) =>
      old?.pages
        ? {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              saved: page.saved?.map((s) => (s.restaurant.id === restaurantId ? { ...s, hearts: patch(s.hearts) } : s)),
            })),
          }
        : old
  );

  queryClient.setQueriesData<{ data?: { listItems?: { restaurantId: string; hearts?: Summary }[] } }>(
    { queryKey: ["lists"] },
    (old) =>
      old?.data?.listItems
        ? {
            ...old,
            data: {
              ...old.data,
              listItems: old.data.listItems.map((i) => (i.restaurantId === restaurantId ? { ...i, hearts: patch(i.hearts) } : i)),
            },
          }
        : old
  );
}

/**
 * Toggle your heart on a restaurant. The table rows and the details query are updated optimistically
 * and then corrected from the server response, so nothing flickers while the request is in flight.
 * Hearting also saves the restaurant, so the saved-restaurants queries refresh afterwards.
 */
export const useToggleHeart = (restaurantId: string) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const key = heartsQueryKey(restaurantId);

  return useMutation({
    mutationFn: (heartIt: boolean) =>
      heartIt ? heartsService.heart(restaurantId) : heartsService.unheart(restaurantId),
    onMutate: async (heartIt) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previousDetail = queryClient.getQueryData<RestaurantHearts>(key);
      const previousSaved = queryClient.getQueriesData({ queryKey: ["saved-restaurants"] });
      const previousLists = queryClient.getQueriesData({ queryKey: ["lists"] });

      patchRowSummaries(queryClient, restaurantId, (prev) =>
        prev.heartedByMe === heartIt ? prev : { count: Math.max(0, prev.count + (heartIt ? 1 : -1)), heartedByMe: heartIt }
      );

      if (previousDetail) {
        const me = user ? { id: user.id, name: user.name, heartedAt: new Date().toISOString() } : null;
        const users = heartIt
          ? me && !previousDetail.users.some((u) => u.id === me.id) ? [me, ...previousDetail.users] : previousDetail.users
          : previousDetail.users.filter((u) => u.id !== user?.id);
        queryClient.setQueryData<RestaurantHearts>(key, { count: users.length, heartedByMe: heartIt, users });
      }
      return { previousDetail, previousSaved, previousLists };
    },
    onError: (_error, _heartIt, context) => {
      if (context?.previousDetail) queryClient.setQueryData(key, context.previousDetail);
      for (const [k, data] of context?.previousSaved ?? []) queryClient.setQueryData(k, data);
      for (const [k, data] of context?.previousLists ?? []) queryClient.setQueryData(k, data);
      toast.error("Could not update your heart. Please try again.");
    },
    onSuccess: (data, heartIt) => {
      // Server state wins; it already matches the optimistic value in the normal case, so nothing visibly changes.
      queryClient.setQueryData(key, data);
      patchRowSummaries(queryClient, restaurantId, () => ({ count: data.count, heartedByMe: data.heartedByMe }));
      // A heart may have saved the restaurant for the first time; pick up that new row quietly.
      if (heartIt) queryClient.invalidateQueries({ queryKey: ["saved-restaurants"], refetchType: "none" });
    },
  });
};
